import { Input } from '@/components/_ui/Input'
import { Separator } from '@/components/_ui/Separator'
import T, { useTranslate } from '@/components/Helpers/Translate'
import { languageTranslations } from '@/constants/pickerRegions/regions'
import type { CountrySearchResult } from '@/lib/hooks/helpers/useCountrySearch'
import { useHandleChangeLocale } from '@/lib/hooks/helpers/useHandleChangeLocale'
import { useCountryLocalization } from '@/lib/hooks/routing/useCountryLocalization'
import type { SupportedLanguage } from '@/lib/utils/localization/getSupportedLanguages'
import { Search } from 'lucide-react'
import Image from 'next/image'

type Props = {
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  searchResults: CountrySearchResult[]
  isSearching: boolean
}

export const SearchCountryInput = ({
  handleSearchChange,
  searchResults,
  isSearching,
}: Props) => {
  const { translate } = useTranslate()

  const { getCountryName } = useCountryLocalization()

  const { handleChangeLocale } = useHandleChangeLocale()

  const renderSearchResults = () => {
    if (!isSearching) return null
    if (searchResults.length === 0) {
      return <div className="my-10">{translate('no_results')}</div>
    }

    const results = () => (
      <div className="mt-5 w-full grid-cols-1 gap-4 md:grid lg:grid-cols-4">
        {searchResults.map((country) => (
          <div className="mb-6 flex items-center space-x-2" key={country.code}>
            <Image
              className="pb-1"
              src={`/flags/${country.code}.svg`}
              alt="Country Flag"
              height={30}
              width={30}
            />
            <div>
              <button
                type="button"
                onClick={() => {
                  ;(async () => {
                    await handleChangeLocale({
                      newAlpha2: country.code,
                      newLanguage: country.languages[0],
                    })
                  })().catch(() => {})
                }}
                className="cursor-pointer text-primary hover:underline"
              >
                {getCountryName(country.code)}
              </button>
              <div className="flex space-x-3">
                {country.languages.map((language: string) => (
                  <button
                    type="button"
                    className="cursor-pointer text-xs text-slate-500 hover:underline"
                    onClick={() => {
                      ;(async () => {
                        await handleChangeLocale({
                          newAlpha2: country.code,
                          newLanguage: language as SupportedLanguage,
                        })
                      })().catch(() => {})
                    }}
                    key={language}
                  >
                    <T>
                      {
                        languageTranslations[
                          language as keyof typeof languageTranslations
                        ]
                      }
                    </T>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    )

    return (
      <div className="my-5">
        <Separator />
        <div>{results()}</div>
      </div>
    )
  }

  return (
    <div>
      <Input
        onChange={(value) => handleSearchChange(value)}
        iconLeft={<Search size={16} />}
        className="w-full lg:max-w-xl"
        label={translate('search')}
      />
      <div>{renderSearchResults()}</div>
    </div>
  )
}
